/**
 * Phone detail phase of the MFA enrollment modal.
 *
 * Presentational only — the parent modal owns the API call.
 */

import { useState } from 'react';
import { Button, Checkbox, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { FormattedMessage, useIntl } from 'react-intl';

import { CountryCallingCodeSelect } from './country-calling-code-select';
import { MfaErrorBanner } from './mfa-error-banner';
import { ModalContent } from '../../components/modal-content';
import { ModalFooter } from '../../components/modal-footer';
import { AUTH0_MFA_TYPES } from '../mfa-constants';
import { getE164Phone } from '../mfa-enrollment-helpers';
import { mfaEnrollmentMessages } from '../mfa-enrollment-messages';
import { mfaMessages } from '../mfa-messages';

import type { CallingCodeOption } from '../mfa-enrollment-helpers';
import type { MfaConsent } from '../mfa-types';
import type { CountryCode } from 'libphonenumber-js';
import type { ChangeEvent, FormEvent } from 'react';

const PHONE_METHODS = [AUTH0_MFA_TYPES.sms, AUTH0_MFA_TYPES.voice] as const;
export type PhoneMethod = (typeof PHONE_METHODS)[number];

const DEFAULT_COUNTRY = 'US' as const;

export type PhoneSetupSubmitPayload = {
  method: PhoneMethod;
  callingCode: string;
  nationalNumber: string;
  consents: MfaConsent[];
};

export type PhoneSetupPhaseProps = {
  /** Which channel the user picked in the method phase; still switchable here. */
  method: PhoneMethod;
  onSubmit: (payload: PhoneSetupSubmitPayload) => void;
  isSubmitting: boolean;
  errorMessage?: string;
  /** Backend error code, rendered alongside `errorMessage`. */
  errorCode?: string;
  /** Consent text is pre-formatted by the consumer, as in `StepUpAuthModal`. */
  consents?: MfaConsent[];
  consentDisclaimer?: string;
};

export const PhoneSetupPhase = ({
  method,
  onSubmit,
  isSubmitting,
  errorMessage,
  errorCode,
  consents = [],
  consentDisclaimer,
}: PhoneSetupPhaseProps) => {
  const intl = useIntl();
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [callingCode, setCallingCode] = useState<string>(getCountryCallingCode(DEFAULT_COUNTRY));
  const [nationalNumber, setNationalNumber] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<string>(method);
  const [acceptedConsentTexts, setAcceptedConsentTexts] = useState<string[]>(
    consents.filter((consent) => consent.accepted).map((consent) => consent.text),
  );
  const [validationError, setValidationError] = useState('');

  const handleCountryChange = (option: CallingCodeOption) => {
    setCountry(option.value);
    setCallingCode(option.callingCode);
    setValidationError('');
  };

  const handleNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNationalNumber(event.currentTarget.value);
    setValidationError('');
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const channel = PHONE_METHODS.find((candidate) => candidate === selectedMethod);
    if (!channel || isSubmitting) {
      return;
    }

    // Normalize local formats (e.g. UK trunk '0') before E.164 validation.
    let normalizedNumber = nationalNumber;
    try {
      const parsed = parsePhoneNumber(nationalNumber, country as CountryCode);
      normalizedNumber = parsed.nationalNumber;
    } catch {
      // not parseable as a local number; raw input goes through validation as-is
    }

    if (!isValidPhoneNumber(getE164Phone(callingCode, normalizedNumber))) {
      setValidationError(intl.formatMessage(mfaEnrollmentMessages.phoneInvalidError));
      return;
    }

    onSubmit({
      method: channel,
      callingCode,
      nationalNumber: normalizedNumber,
      consents: consents.map((consent) => ({ ...consent, accepted: acceptedConsentTexts.includes(consent.text) })),
    });
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <ModalContent>
        <Text fw={600} size='lg'>
          <FormattedMessage {...mfaEnrollmentMessages.phoneTitle} />
        </Text>

        <SegmentedControl
          aria-label={intl.formatMessage(mfaEnrollmentMessages.phoneChannelLabel)}
          data={[
            { value: AUTH0_MFA_TYPES.sms, label: intl.formatMessage(mfaEnrollmentMessages.methodSms) },
            { value: AUTH0_MFA_TYPES.voice, label: intl.formatMessage(mfaEnrollmentMessages.methodVoice) },
          ]}
          disabled={isSubmitting}
          onChange={setSelectedMethod}
          value={selectedMethod}
        />

        <CountryCallingCodeSelect disabled={isSubmitting} onChange={handleCountryChange} value={country} />

        <TextInput
          disabled={isSubmitting}
          error={validationError === '' ? undefined : validationError}
          label={intl.formatMessage(mfaEnrollmentMessages.phoneLabel)}
          onChange={handleNumberChange}
          type='tel'
          value={nationalNumber}
        />

        {consents.length > 0 ? (
          <Stack gap='xs'>
            <Checkbox.Group onChange={setAcceptedConsentTexts} value={acceptedConsentTexts}>
              <Stack gap='xs'>
                {consents.map((consent) => (
                  <Checkbox key={consent.text} disabled={isSubmitting} label={consent.text} value={consent.text} />
                ))}
              </Stack>
            </Checkbox.Group>

            {consentDisclaimer ? (
              <Text c='dimmed' size='xs'>
                {consentDisclaimer}
              </Text>
            ) : null}
          </Stack>
        ) : null}

        {errorMessage ? <MfaErrorBanner code={errorCode} message={errorMessage} /> : null}
      </ModalContent>

      <ModalFooter>
        <Button disabled={isSubmitting} type='submit'>
          <FormattedMessage {...mfaMessages.sendCodeButton} />
        </Button>
      </ModalFooter>
    </form>
  );
};
